/**
 * "You may also like" — paged related products at the bottom of ProductDetail.
 *
 * - Loads first 8 immediately.
 * - IntersectionObserver on a sentinel element fetches the next 8 each time
 *   the strip's end scrolls into view, up to 100 items total.
 * - No spinner: new items fade in via CSS keyframes (see RecMiniCard).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { getRelatedProducts } from '../../api/storeApi';
import RecMiniCard from '../../Components/RecMiniCard';
import useStripScroll from '../../hooks/useStripScroll';
import { getBasePath } from '../../utils/domainMode';
import styles from './lazyRelated.module.css';

const PAGE_SIZE = 10;
const HARD_LIMIT = 100;

/**
 * ON A PHONE THIS IS A GRID, NOT A STRIP (2026-10-07, the owner: "didn't we say we will show
 * it in list like the catalogue, not a strip?").
 *
 * A 148px card in a horizontal strip shows one and a half products on a 390px screen, and the
 * rest are behind a gesture. The same height as a two-column grid shows four — the shape every
 * shopper already knows from the catalogue they arrived through. On a desktop the strip stays:
 * it already shows six or seven cards at once, it has arrows for a mouse, and it bleeds to the
 * window — none of which a phone gets.
 *
 * The grid is CAPPED. Lazy paging down a vertical grid is an infinite scroll, and the footer,
 * the privacy notice and the shop's own links sit underneath it — a shopper would never reach
 * them. Eight (four rows), then a door to the whole catalogue.
 */
const GRID_CAP = 8;

const LazyRelated = ({ salonSlug, productId, onAddToCart, campaigns }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [done, setDone] = useState(false);
  const offsetRef = useRef(0);
  const fetchingRef = useRef(false);
  const sentinelRef = useRef(null);
  const { ref: stripRef, canPrev, canNext, scrollPrev, scrollNext, refresh } = useStripScroll();
  // The same probe CartBar uses. A browser without matchMedia (and jsdom) reads as a phone,
  // which is the layout that needs no arrows and no bleed — the safe way to be wrong.
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.matchMedia?.('(min-width: 900px)').matches === true,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 900px)');
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    if (mq.addEventListener) { mq.addEventListener('change', onChange); return () => mq.removeEventListener('change', onChange); }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const fetchPage = async () => {
    if (fetchingRef.current || done) return;
    if (offsetRef.current >= HARD_LIMIT) { setDone(true); return; }
    fetchingRef.current = true;
    try {
      const next = await getRelatedProducts(salonSlug, productId, {
        limit: PAGE_SIZE, offset: offsetRef.current,
      });
      if (!next || next.length === 0) {
        setDone(true);
      } else {
        setItems((prev) => {
          // Dedupe (server may return the same product across pages if state
          // changes mid-scroll; we never want to render duplicates).
          const seen = new Set(prev.map((p) => p.id));
          const fresh = next.filter((p) => !seen.has(p.id));
          return prev.concat(fresh);
        });
        offsetRef.current += next.length;
        if (next.length < PAGE_SIZE) setDone(true);
      }
    } finally {
      fetchingRef.current = false;
    }
  };

  // First page on mount / product change.
  useEffect(() => {
    setItems([]); setDone(false); offsetRef.current = 0;
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonSlug, productId]);

  // IntersectionObserver to fetch more as the sentinel scrolls into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !isDesktop) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) fetchPage();
    }, { rootMargin: '200px' });
    obs.observe(node);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonSlug, productId, done, isDesktop]);

  // Recompute arrow visibility once new pages land.
  useEffect(() => { refresh(); }, [items.length, refresh]);

  if (items.length === 0 && done) return null;

  const shown = isDesktop ? items : items.slice(0, GRID_CAP);
  const moreThanShown = !isDesktop && (items.length > GRID_CAP || !done);

  return (
    <section className={styles.section} data-testid="product-detail-related">
      <h2 className={styles.title}>{t('rec.youMayAlsoLike', { defaultValue: 'You may also like' })}</h2>
      <div className={styles.stripWrap}>
        {isDesktop && canPrev && (
          <button
            type="button"
            className={`${styles.scrollArrow} ${styles.scrollArrowPrev}`}
            onClick={scrollPrev}
            aria-label={t('common.scrollPrev', { defaultValue: 'Scroll backward' })}
            data-testid="product-detail-related-prev"
          >
            <FiChevronLeft />
          </button>
        )}
        {isDesktop && canNext && (
          <button
            type="button"
            className={`${styles.scrollArrow} ${styles.scrollArrowNext}`}
            onClick={scrollNext}
            aria-label={t('common.scrollNext', { defaultValue: 'Scroll forward' })}
            data-testid="product-detail-related-next"
          >
            <FiChevronRight />
          </button>
        )}
        {/* The fade is only ever on a side that HAS more behind it — a strip
          * sitting at its start with its start edge faded says "there is
          * something back there" when there is not. Same two flags the arrows
          * already run on. */}
        <div
          className={[
            isDesktop ? styles.strip : styles.grid,
            isDesktop && canPrev ? styles.stripFadeStart : '',
            isDesktop && canNext ? styles.stripFadeEnd : '',
          ].filter(Boolean).join(' ')}
          role="list"
          data-layout={isDesktop ? 'strip' : 'grid'}
          ref={stripRef}
        >
          {shown.map((p) => (
            <RecMiniCard key={p.id} product={p} salonSlug={salonSlug} onAddToCart={onAddToCart} campaigns={campaigns} />
          ))}
          {isDesktop && !done && <div ref={sentinelRef} className={styles.sentinel} aria-hidden />}
        </div>
      </div>
      {moreThanShown && (
        /* THE SHOP, NOT THE LANDING (2026-10-10). Thirty days of DK's product pages: most exits went "home" — and this
           link, which says "see everything in the shop", sent people to the landing page too. The catalogue is
           `/shop`; the words were right and the address was not. */
        <Link
          to={`${getBasePath(salonSlug)}/shop`}
          className={styles.seeAll}
          data-testid="product-detail-related-see-all"
        >
          {t('rec.seeEverything', { defaultValue: 'See everything in the shop' })}
        </Link>
      )}
    </section>
  );
};

export default LazyRelated;
